<script lang="ts" setup>
import { useAuthStore } from '~/stores/auth'
import { RoutesEnum } from '~/enums'
import { ResponseStatus } from '@kira-dancer/nuxt-common/enums'
import type { ResetPasswordRequestPayload } from '@kira-dancer/nadal'
import { useForgotPasswordReset } from '~/composables/form/use-forgot-password'
import type { ResponseError } from '~/types/app'
import { MESSAGE_UNAUTHORIZED } from '~/constants'

const { navigate } = useNavigate()
const { onResetPassword } = useAccount()
const authStore = useAuthStore()

const isLoading = ref(false)

const showSuccess = ref(false)

const onSuccess = () => {
  authStore.forgotPasswordStepReset = false
  showSuccess.value = true
  navigate(RoutesEnum.Home)
}

const { meta, isSubmitting, handleSubmit } = useForgotPasswordReset()

const disableBtn = computed(
  () =>
    !meta.value.dirty ||
    !meta.value.valid ||
    isLoading.value ||
    isSubmitting.value,
)

const loading = computed(() => isLoading.value || isSubmitting.value)
const showError = ref(false)
const errorMessage = ref('')

const onOpenErrorModal = (message: string) => {
  authStore.forgotPasswordStepReset = false
  errorMessage.value = message
  showError.value = true
}

const handleBackToHome = () => {
  authStore.forgotPasswordStepReset = false
  navigate(RoutesEnum.Home)
}

const onRetry = () => {
  showError.value = false
  errorMessage.value = ''
  authStore.forgotPasswordStepReset = true
}

const onSubmit = handleSubmit(async (formData: ResetPasswordRequestPayload) => {
  try {
    isLoading.value = true
    const response = await onResetPassword(formData)
    const { status } = response.value
    if (status === ResponseStatus.OK) {
      onSuccess()
      return
    }
  } catch (error) {
    const dataError = error as ResponseError
    if (
      dataError.status === ResponseStatus.AUTHENTICATION_REQUIRED_ERROR ||
      MESSAGE_UNAUTHORIZED === dataError.message
    ) {
      onOpenErrorModal('Link đổi mật khẩu không hợp lệ.')
      return
    }

    if (dataError.message) {
      onOpenErrorModal(dataError.message?.replaceAll(',', ', \n'))
    }
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div>
    <BaseModal
      v-model="authStore.forgotPasswordStepReset"
      :title="`Đặt lại mật khẩu`"
      @on-button-close="handleBackToHome"
    >
      <form class="form-forgot-password" action="#" @submit.prevent="onSubmit">
        <FormPassword
          name="password"
          label="Mật khẩu mới"
          placeholder="Nhập mật khẩu mới của bạn"
          :max-length="32"
        />
        <FormPassword
          class="confirm-password"
          name="confirmPassword"
          label="Xác nhận mật khẩu"
          placeholder="Xác nhận mật khẩu mới của bạn"
          :max-length="32"
        />
        <BaseButton
          custom-class="submit-btn"
          type="submit"
          variant="primary"
          :disabled="disableBtn"
          :loading="loading"
          button-id="create-new-password"
          button-name="create-new-password"
          label="Tạo mật khẩu"
        />
      </form>
    </BaseModal>
    <BaseConfirm
      v-model="showError"
      title="Thất bại"
      :confirm-label="`Thử lại`"
      image="/images/popup/failed.svg"
      @on-confirm="onRetry"
      @on-button-close="handleBackToHome"
    >
      <template #description>
        <span v-if="errorMessage" class="whitespace-pre-wrap">{{
          errorMessage
        }}</span>
      </template>
    </BaseConfirm>
    <BaseConfirm
      v-model="showSuccess"
      :timeout="2000"
      title="Thành công"
      description="Cập nhật thông tin thành công."
      image="/images/popup/success.svg"
    />
  </div>
</template>
<style
  lang="scss"
  src="/assets/styles/components/desktop/form/forgot-password-reset.scss"
  scoped
></style>
